import React, { useCallback, useEffect, useMemo } from 'react';
import { useSharedValue } from 'react-native-reanimated';

import { PlayerProjectionFragment } from '@/api/events/query.generated';
import { Box } from '@/components/lib/components';
import BetrAnalytics from '@/feature/analytics/analytics';
import { usePrevious } from '@/hooks/use-previous';
import { MarketOption, MarketStatus } from '@/types/api.generated';
import { sortAndGroupProjectionsByType, sortProjections } from '@/utils/sortProjections';

import { AnalyticsEvent } from '../analytics/constants';
import { useBetslipStore } from '../betslip-pickem/hooks/use-betslip-store';
import { allPlayerPropsSelector, usePlayerPropsStore } from '../betslip-pickem/hooks/use-player-props-store';
import { PlayerWithTeamAndEvent } from '../betslip-pickem/types';
import { isPlayerSelected } from '../betslip-pickem/utils/betslip-utils';
import { usePickSelection } from '../lobby/hooks/use-pick-selection';
import { checkIfDiscountedProjection } from '../lobby/utils/lineupsUtils';
import { POPULAR_PLAYERS_FILTER } from '../lobby/utils/players';
import { AlternateProjectionValues } from './AlternateProjectionValues';
import { DiscountedProjectionCard } from './DiscountedProjectionCard';
import { usePlayerProfileDataStore } from './hooks/use-player-profile-data-store';

export const ProjectionsList = ({
    player,
    analyticsTag,
}: {
    player: PlayerWithTeamAndEvent;
    analyticsTag?: string;
}) => {
    const projections = usePlayerPropsStore(allPlayerPropsSelector(player.id));
    const playerProjectionFilter = usePlayerProfileDataStore(state => state.selectedPlayerProjectionFilter);
    const popularPlayerProjection = usePlayerProfileDataStore(state => state.popularPlayerProjection);

    const groupedProjections = useMemo(() => {
        const sortedProjections = projections.toSorted(sortProjections);
        const filteredProjections = sortedProjections.filter(
            projection => !checkIfDiscountedProjection(projection.type)
        );
        const projectionFilter =
            playerProjectionFilter === POPULAR_PLAYERS_FILTER ? popularPlayerProjection : playerProjectionFilter;

        return sortAndGroupProjectionsByType(filteredProjections, projectionFilter);
    }, [playerProjectionFilter, popularPlayerProjection, projections]);

    const availableGroupedProjections: Record<string, PlayerProjectionFragment[]> = useMemo(() => {
        return Object.entries(groupedProjections).reduce((acc, [groupKey, groupProjections]) => {
            const allSuspended = groupProjections.every(
                projection => projection.marketStatus === MarketStatus.Suspended
            );

            if (allSuspended) {
                // Keep entire group of projections as is (for UI suspended state on row)
                acc[groupKey] = groupProjections;
            } else {
                // Filter out suspended projections from this group and show only opened ones
                const activeProjections = groupProjections.filter(
                    projection => projection.marketStatus !== MarketStatus.Suspended
                );

                if (activeProjections.length > 0) {
                    acc[groupKey] = activeProjections;
                }
            }

            return acc;
        }, {} as Record<string, PlayerProjectionFragment[]>);
    }, [groupedProjections]);

    const discountProjections = player?.projections?.filter(projection => checkIfDiscountedProjection(projection.type));

    const eventId = player.event.id;
    const playerPicked = isPlayerSelected({ eventId, playerId: player.id });
    const { removeSelection, makeSelection } = usePickSelection();
    const betslip = useBetslipStore(state => state.betslip);
    const prevPlayer = usePrevious(player.id);

    const initialExpandedIndex = useMemo(() => {
        // always expand the first projection group if it has multiple projection types and at least one is not suspended
        if (Object.keys(availableGroupedProjections).length > 0) {
            const firstProjection = Object.keys(availableGroupedProjections)[0];
            const firstProjectionGroup = availableGroupedProjections[firstProjection] || [];
            const allSuspended = firstProjectionGroup?.every(p => p.marketStatus === MarketStatus.Suspended);

            if (firstProjectionGroup.length > 1 && !allSuspended) {
                return 0;
            }
            return -1;
        }
        return -1;
    }, [availableGroupedProjections]);
    const expandedIndex = useSharedValue<number>(-1);

    useEffect(() => {
        if (prevPlayer !== player.id) {
            // When opening a single player profile, the expandedIndex would keep it's state
            // even when opening a different player. This ensures we update it correctly.
            expandedIndex.value = initialExpandedIndex;
        }
    }, [expandedIndex, initialExpandedIndex, player.id, prevPlayer]);

    const removePlayer = useCallback(() => {
        if (eventId && player) {
            removeSelection([{ eventId, player: player }]);
        }
    }, [eventId, player, removeSelection]);

    const selectMarketOption = useCallback(
        (projection: PlayerProjectionFragment, option: MarketOption) => {
            const successfulSelection = makeSelection(
                {
                    player,
                    eventId,
                    outcome: option.outcome,
                    projection,
                },
                analyticsTag
            );
            if (successfulSelection) {
                if (analyticsTag && betslip.length === 1) {
                    BetrAnalytics.trackEvent(AnalyticsEvent.ADD_1ST_PICK, { location: analyticsTag });
                }
            }
        },
        [makeSelection, player, eventId, analyticsTag, betslip.length]
    );

    if (Object.keys(availableGroupedProjections).length === 0 && discountProjections.length === 0) {
        return null;
    }

    //We want to hide the projection expand buttons when all projections are suspended or have no alternate lines
    const allProjectionGroupsSingleOrSuspended = Object.keys(availableGroupedProjections).every(projectionKey => {
        const single = availableGroupedProjections[projectionKey].length === 1;
        const suspended = availableGroupedProjections[projectionKey].every(
            it => it.marketStatus === MarketStatus.Suspended
        );
        return single || suspended;
    });

    return (
        <Box overflow={'hidden'}>
            {discountProjections.map(projection => {
                return (
                    <DiscountedProjectionCard
                        projection={projection}
                        event={player.event}
                        key={projection.marketId}
                        playerPicked={playerPicked}
                        removePlayer={removePlayer}
                        selectMarketOption={selectMarketOption}
                    />
                );
            })}
            {Object.keys(availableGroupedProjections).map((projectionType, index) => {
                const projectionGroup = availableGroupedProjections[projectionType];
                if (projectionGroup.length === 0) {
                    return null;
                }
                return (
                    <AlternateProjectionValues
                        key={`${projectionType}-${player.id}`}
                        league={player.league}
                        sport={player.sport}
                        projectionsGroup={projectionGroup}
                        index={index}
                        hideExpandButton={allProjectionGroupsSingleOrSuspended}
                        expandedIndex={expandedIndex}
                        groupsCount={Object.keys(availableGroupedProjections).length}
                        removePlayer={removePlayer}
                        selectMarketOption={selectMarketOption}
                        playerPicked={playerPicked}
                    />
                );
            })}
        </Box>
    );
};
